/**
 * Juwelier Reichart · Uhren-Vergleich.
 * Zeigt bis zu drei Uhren maßstabsgetreu nebeneinander (Millimeter-SVG wie die Anprobe), wahlweise auf einem Handgelenk
 * mit dem Umfang und Hautton aus der Anprobe (localStorage „jr-anprobe“), und darunter eine Angabentabelle.
 * Auswahl: ?u=handle1,handle2 → sonst zuletzt gewählte Uhren (localStorage „jr-vergleich“) → sonst die ersten zwei.
 */

const SVG = 'http://www.w3.org/2000/svg';
const HAUTTOENE = ['#f3d5c0', '#e6bc98', '#cf9b74', '#b07650', '#8a5638', '#5c3a27'];
const MITTE_Y = 56;

/** @param {string} name @param {Record<string, string|number>} [attr] */
function el(name, attr = {}) {
  const e = document.createElementNS(SVG, name);
  for (const [k, v] of Object.entries(attr)) e.setAttribute(k, String(v));
  return e;
}

/** @param {string} hex @param {number} faktor */
function ton(hex, faktor) {
  const n = parseInt(hex.slice(1), 16);
  const kanal = (/** @type {number} */ v) => Math.max(0, Math.min(255, Math.round(faktor < 1 ? v * faktor : v + (255 - v) * (faktor - 1))));
  return `rgb(${kanal(n >> 16)} ${kanal((n >> 8) & 255)} ${kanal(n & 255)})`;
}

/** @param {number|null|undefined} z */
const mm = (z) => (z == null ? '–' : `${String(z).replace('.', ',')} mm`);

/** Angaben aus dem Beschreibungstext (Gambio-Format „Merkmal: Wert“). @param {string} t */
function angaben(t) {
  const zahl = (/** @type {RegExp} */ r) => {
    const m = t.match(r);
    return m ? m[1] : null;
  };
  return {
    dicke: zahl(/Dicke:?\s*(\d+(?:,\d+)?)\s*mm/i),
    gewicht: zahl(/Gewicht:?\s*(\d+(?:,\d+)?)\s*g\b/i),
    wasser: zahl(/(\d+)\s*ATM/i),
    werk: /automatik/i.test(t) ? 'Automatik' : /chrono/i.test(t) ? 'Chronograph, Quarz' : /quarz/i.test(t) ? 'Quarz' : null,
    glas: /saphirglas/i.test(t) ? 'Saphirglas' : /mineralglas/i.test(t) ? 'Mineralglas' : null,
  };
}

class JrVergleich extends HTMLElement {
  connectedCallback() {
    if (this.dataset.bereit) return;
    this.dataset.bereit = '1';
    /** @type {any[]} */
    this.uhren = JSON.parse(this.querySelector('[data-uhren]')?.textContent || '[]');
    this.max = Number(this.dataset.max || 3);
    let anprobe = {};
    try {
      anprobe = JSON.parse(localStorage.getItem('jr-anprobe') || '{}');
    } catch {}
    this.umfang = /** @type {any} */ (anprobe).umfang || 17;
    this.haut = /** @type {any} */ (anprobe).haut ?? 1;

    const aus = new URLSearchParams(location.search).get('u');
    let gemerkt = [];
    try {
      gemerkt = JSON.parse(localStorage.getItem('jr-vergleich') || '[]');
    } catch {}
    const vorhanden = new Set(this.uhren.map((u) => u.handle));
    /** @type {string[]} */
    this.wahl = (aus ? aus.split(',') : gemerkt).filter((h) => vorhanden.has(h)).slice(0, this.max);
    if (aus && this.wahl.length === 1) {
      for (const h of gemerkt) if (vorhanden.has(h) && !this.wahl.includes(h) && this.wahl.length < 2) this.wahl.push(h);
    }
    if (!this.wahl.length) this.wahl = this.uhren.slice(0, 2).map((u) => u.handle);

    this.chipsBauen();
    const regler = /** @type {HTMLInputElement} */ (this.querySelector('[data-umfang]'));
    regler.value = String(this.umfang);
    regler.addEventListener('input', () => {
      this.umfang = parseFloat(regler.value);
      this.zeichnen();
    });
    this.querySelector('[data-handgelenk]')?.addEventListener('change', () => this.zeichnen());
    this.zeichnen();
  }

  chipsBauen() {
    const ziel = this.querySelector('[data-chips]');
    if (!ziel) return;
    ziel.replaceChildren();
    for (const u of this.uhren) {
      const knopf = document.createElement('button');
      knopf.type = 'button';
      knopf.className = 'jr-vergleich__chip';
      knopf.dataset.handle = u.handle;
      const bild = document.createElement('img');
      bild.src = u.vorschau;
      bild.alt = '';
      bild.loading = 'lazy';
      bild.width = 64;
      bild.height = 64;
      knopf.append(bild, document.createTextNode(u.titel.replace(/^a\.b\.art\s*/i, '')));
      knopf.addEventListener('click', () => this.umschalten(u.handle));
      ziel.append(knopf);
    }
  }

  /** @param {string} handle */
  umschalten(handle) {
    this.wahl = this.wahl.includes(handle) ? this.wahl.filter((h) => h !== handle) : [...this.wahl, handle].slice(-this.max);
    try {
      localStorage.setItem('jr-vergleich', JSON.stringify(this.wahl));
    } catch {}
    const url = new URL(location.href);
    url.searchParams.set('u', this.wahl.join(','));
    history.replaceState(null, '', url);
    this.zeichnen();
  }

  zeichnen() {
    for (const knopf of /** @type {NodeListOf<HTMLButtonElement>} */ (this.querySelectorAll('.jr-vergleich__chip'))) {
      const an = this.wahl.includes(/** @type {string} */ (knopf.dataset.handle));
      knopf.setAttribute('aria-pressed', String(an));
    }
    const zaehler = this.querySelector('[data-zaehler]');
    if (zaehler) zaehler.textContent = `(${this.wahl.length} von ${this.max})`;
    const umfangText = this.querySelector('[data-umfang-text]');
    if (umfangText) umfangText.textContent = `${this.umfang.toFixed(1).replace('.', ',')} cm`;
    const leer = /** @type {HTMLElement} */ (this.querySelector('[data-leer]'));
    leer.hidden = this.wahl.length > 0;

    const gewaehlt = this.wahl.map((h) => this.uhren.find((u) => u.handle === h)).filter(Boolean);
    this.buehne(gewaehlt);
    this.tabelle(gewaehlt);
  }

  /** @param {any[]} uhren */
  buehne(uhren) {
    const svg = /** @type {SVGSVGElement} */ (this.querySelector('[data-buehne]'));
    const amArm = /** @type {HTMLInputElement} */ (this.querySelector('[data-handgelenk]')).checked;
    const hg = this.umfang * 10 * 0.345;
    const haut = HAUTTOENE[this.haut] || HAUTTOENE[1];
    const abstand = 18;
    const slots = uhren.map((u) => {
      const a = u.anprobe;
      const breite = a.gehaeuse_mm ? a.gehaeuse_mm / a.gehaeuse_anteil : (a.band_mm || 20) / a.band_anteil;
      return { u, a, breite, gehaeuse: a.gehaeuse_mm || breite * a.gehaeuse_anteil, platz: Math.max(56, breite * a.gehaeuse_anteil + 22) };
    });
    const gesamt = slots.reduce((s, x) => s + x.platz, 0) + abstand * (slots.length + 1);
    const breiteSvg = Math.max(gesamt, 150);
    // Höhe nach Inhalt: am Handgelenk nur das Band, sonst das ganze Bild; darunter Platz für Maßlinie und Name
    const halbeHoehe = amArm ? hg / 2 + 4 : Math.max(...slots.map((s) => (s.breite * s.a.seitenverhaeltnis) / 2), hg / 2) + 2;
    const oben = MITTE_Y - halbeHoehe;
    const unten = MITTE_Y + halbeHoehe + 8 + 12.5 / (Math.min(svg.parentElement?.clientWidth || 600, breiteSvg * 3.78) / breiteSvg) * 1.4;
    svg.setAttribute('viewBox', `0 ${oben.toFixed(1)} ${breiteSvg.toFixed(1)} ${(unten - oben).toFixed(1)}`);
    // ≈ 3,8 CSS-Pixel je Millimeter, damit die Uhren auf großen Bildschirmen nicht übergroß werden
    svg.style.width = `min(100%, ${(breiteSvg * 3.78).toFixed(0)}px)`;
    // Beschriftung immer etwa 12 px groß, unabhängig davon, wie stark die Bühne skaliert wird
    const pxJeMm = Math.min(svg.parentElement?.clientWidth || 600, breiteSvg * 3.78) / breiteSvg;
    const schrift = 12.5 / pxJeMm;
    svg.replaceChildren();

    const defs = el('defs');
    const verlauf = el('linearGradient', { id: 'jrVglHaut', x1: 0, y1: 0, x2: 0, y2: 1 });
    [[0, ton(haut, 0.74)], [0.22, ton(haut, 0.97)], [0.45, ton(haut, 1.07)], [0.7, haut], [1, ton(haut, 0.7)]].forEach(([o, c]) => verlauf.append(el('stop', { offset: o, 'stop-color': c })));
    const schatten = el('filter', { id: 'jrVglSchatten', x: -0.2, y: -0.2, width: 1.4, height: 1.4 });
    schatten.append(el('feDropShadow', { dx: 0.6, dy: 1.4, stdDeviation: 1.2, 'flood-color': '#000', 'flood-opacity': 0.3 }));
    const clip = el('clipPath', { id: 'jrVglArm' });
    clip.append(el('rect', { x: 0, y: MITTE_Y - hg / 2, width: breiteSvg, height: hg, rx: 0 }));
    const wickel = el('linearGradient', { id: 'jrVglWickel', x1: 0, y1: 0, x2: 0, y2: 1 });
    [[0, 0.5], [0.14, 0.12], [0.3, 0], [0.7, 0], [0.86, 0.15], [1, 0.55]].forEach(([o, a]) => wickel.append(el('stop', { offset: o, 'stop-color': '#000', 'stop-opacity': a })));
    defs.append(verlauf, schatten, clip, wickel);
    svg.append(defs);

    if (amArm) {
      svg.append(el('rect', { x: -2, y: MITTE_Y - hg / 2, width: breiteSvg + 4, height: hg, fill: 'url(#jrVglHaut)' }));
    }

    let x = abstand;
    for (const s of slots) {
      const mitte = x + s.platz / 2;
      const hoehe = s.breite * s.a.seitenverhaeltnis;
      const g = el('g', amArm ? { 'clip-path': 'url(#jrVglArm)' } : {});
      g.append(el('image', { href: s.a.bild, x: (mitte - s.a.mitte_x * s.breite).toFixed(2), y: (MITTE_Y - s.a.mitte_y * hoehe).toFixed(2), width: s.breite.toFixed(2), height: hoehe.toFixed(2), filter: 'url(#jrVglSchatten)' }));
      if (amArm) {
        const band = s.a.band_anteil * s.breite;
        g.append(el('rect', { x: (mitte - band / 2 - 1).toFixed(2), y: MITTE_Y - hg / 2, width: (band + 2).toFixed(2), height: hg, fill: 'url(#jrVglWickel)', style: 'mix-blend-mode:multiply' }));
      }
      svg.append(g);

      // Maßlinie unter dem Gehäuse
      const halb = s.gehaeuse / 2;
      const yLinie = MITTE_Y + halbeHoehe + 4;
      const mass = el('g', { stroke: '#8a6418', 'stroke-width': 0.3, fill: 'none' });
      mass.append(el('path', { d: `M${mitte - halb},${yLinie - 2.5}v5M${mitte + halb},${yLinie - 2.5}v5M${mitte - halb},${yLinie}H${mitte + halb}` }));
      svg.append(mass);
      const beschriftung = el('text', { x: mitte, y: yLinie + 2 + schrift * 1.1, 'text-anchor': 'middle', 'font-size': schrift.toFixed(2), fill: '#1a1712', 'font-family': 'var(--jr-schrift-text, sans-serif)' });
      beschriftung.textContent = `${s.u.titel.replace(/^a\.b\.art\s*/i, '')} · ${s.a.gehaeuse_mm ? `ø ${String(s.a.gehaeuse_mm).replace('.', ',')} mm` : `Band ${String(s.a.band_mm).replace('.', ',')} mm`}`;
      svg.append(beschriftung);
      x += s.platz + abstand;
    }
  }

  /** @param {any[]} uhren */
  tabelle(uhren) {
    const t = /** @type {HTMLTableElement} */ (this.querySelector('[data-tabelle]'));
    t.replaceChildren();
    if (!uhren.length) return;
    const daten = uhren.map((u) => ({ u, ...angaben(u.text) }));
    const kopf = t.createTHead().insertRow();
    kopf.append(document.createElement('th'));
    for (const d of daten) {
      const th = document.createElement('th');
      th.scope = 'col';
      const a = document.createElement('a');
      a.href = d.u.url;
      a.textContent = d.u.titel;
      th.append(a);
      kopf.append(th);
    }
    const zeilen = [
      ['Preis', (/** @type {any} */ d) => d.u.preis],
      ['Gehäuse', (/** @type {any} */ d) => (d.u.anprobe.gehaeuse_mm ? `ø ${mm(d.u.anprobe.gehaeuse_mm)}` : null)],
      ['Dicke', (/** @type {any} */ d) => (d.dicke ? `${d.dicke} mm` : null)],
      ['Gewicht', (/** @type {any} */ d) => (d.gewicht ? `${d.gewicht} g` : null)],
      ['Bandbreite', (/** @type {any} */ d) => (d.u.anprobe.band_mm ? mm(d.u.anprobe.band_mm) : null)],
      ['Werk', (/** @type {any} */ d) => d.werk],
      ['Glas', (/** @type {any} */ d) => d.glas],
      ['Wasserdicht', (/** @type {any} */ d) => (d.wasser ? `${d.wasser} ATM` : null)],
      ['Für', (/** @type {any} */ d) => d.u.typ],
    ];
    const rumpf = t.createTBody();
    for (const [name, wert] of zeilen) {
      const werte = daten.map((d) => /** @type {Function} */ (wert)(d));
      if (werte.every((w) => !w)) continue;
      const zeile = rumpf.insertRow();
      const th = document.createElement('th');
      th.scope = 'row';
      th.textContent = /** @type {string} */ (name);
      zeile.append(th);
      const verschieden = new Set(werte.map((w) => w || '–')).size > 1 && daten.length > 1;
      for (const w of werte) {
        const td = zeile.insertCell();
        td.textContent = w || '–';
        if (verschieden) td.className = 'jr-vergleich__unterschied';
      }
    }
    const fuss = rumpf.insertRow();
    fuss.append(document.createElement('th'));
    for (const d of daten) {
      const td = fuss.insertCell();
      const a = document.createElement('a');
      a.href = d.u.url;
      a.className = 'jr-link';
      a.textContent = 'Zur Uhr';
      td.append(a);
    }
  }
}

if (!customElements.get('jr-vergleich')) customElements.define('jr-vergleich', JrVergleich);
